import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { CheckCircle, XCircle, Clock, Play, RefreshCw, AlertTriangle, Shield, Navigation, LogIn, Monitor } from 'lucide-react';

const ALL_SCREENS = [
  { path: '/', name: 'Home Landing Page', category: 'Public' },
  { path: '/login', name: 'Login', category: 'Public' },
  { path: '/register', name: 'Register', category: 'Public' },
  { path: '/privacy-policy', name: 'Privacy Policy', category: 'Public' },
  { path: '/terms-of-service', name: 'Terms of Service', category: 'Public' },
  { path: '/security', name: 'Security', category: 'Public' },
  { path: '/reset-password', name: 'Reset Password', category: 'Public' },
  { path: '/dashboard', name: 'Dashboard', category: 'Protected' },
  { path: '/profile-settings', name: 'Profile Settings', category: 'Protected' },
  { path: '/subscription-management', name: 'Subscription Management', category: 'Protected' },
  { path: '/billing-history', name: 'Billing History', category: 'Protected' },
  { path: '/help-support', name: 'Help & Support', category: 'Protected' },
  { path: '/communication-hub', name: 'Communication Hub', category: 'Core Features' },
  { path: '/lead-management', name: 'Lead Management', category: 'Core Features' },
  { path: '/lead-discovery', name: 'Lead Discovery', category: 'Core Features' },
  { path: '/funnel-management', name: 'Funnel Management', category: 'Core Features' },
  { path: '/funnel-builder', name: 'Funnel Builder', category: 'Core Features' },
  { path: '/calendar-scheduling', name: 'Calendar Scheduling', category: 'Core Features' },
  { path: '/analytics-dashboard', name: 'Analytics Dashboard', category: 'Core Features' },
  { path: '/workflow-management', name: 'Workflow Management', category: 'Core Features' },
  { path: '/workflow-builder', name: 'Workflow Builder', category: 'Core Features' },
  { path: '/ai-build-marketing-funnels', name: 'AI Build Marketing Funnels', category: 'AI Features' },
  { path: '/ai-sales-closer', name: 'AI Sales Closer', category: 'AI Features' },
  { path: '/ai-lead-finder-and-scrubber', name: 'AI Lead Finder & Scrubber', category: 'AI Features' },
  { path: '/ai-template-studio', name: 'AI Template Studio', category: 'AI Features' },
  { path: '/ai-lead-scoring-system', name: 'AI Lead Scoring System', category: 'AI Features' },
  { path: '/roi-tracking-dashboard', name: 'ROI Tracking Dashboard', category: 'AI Features' },
  { path: '/ai-person-finder', name: 'AI Person Finder', category: 'AI Features' },
  { path: '/gohighlevel-integration', name: 'GoHighLevel Integration', category: 'AI Features' },
  { path: '/ai-dialer', name: 'AI Dialer', category: 'AI Features' },
  { path: '/cross-platform-integration-hub', name: 'Cross-Platform Integration Hub', category: 'Advanced' },
  { path: '/automation-workflow-designer', name: 'Automation Workflow Designer', category: 'Advanced' },
  { path: '/integration-analytics-monitoring', name: 'Integration Analytics Monitoring', category: 'Advanced' },
  { path: '/sales-funnel-management', name: 'Sales Funnel Management', category: 'Advanced' },
  { path: '/funnel-analytics', name: 'Funnel Analytics', category: 'Advanced' },
  { path: '/api-keys-management', name: 'API Keys Management', category: 'Advanced' },
  { path: '/api-keys', name: 'API Keys', category: 'Advanced' },
  { path: '/system-diagnostics', name: 'System Diagnostics', category: 'Advanced' },
];

const DEMO_EMAIL = 'demo@leadgenproai.us';
const DEMO_PASSWORD = 'Demo1234!';

const STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  PASS: 'pass',
  FAIL: 'fail',
  SKIP: 'skip',
};

const PHASE = {
  IDLE: 'idle',
  LOGIN: 'login',
  NAVIGATION: 'navigation',
  AUTH_REFRESH: 'auth_refresh',
  AUTH_RESTART: 'auth_restart',
  COMPLETE: 'complete',
};

export default function AutomatedTestFlow() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(PHASE?.IDLE);
  const [loginResult, setLoginResult] = useState(null);
  const [screenResults, setScreenResults] = useState(
    ALL_SCREENS?.map(s => ({ ...s, status: STATUS?.PENDING, duration: null, error: null }))
  );
  const [authRefreshResult, setAuthRefreshResult] = useState(null);
  const [authRestartResult, setAuthRestartResult] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(null);
  const [overallStatus, setOverallStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const abortRef = useRef(false);
  const logEndRef = useRef(null);

  const addLog = useCallback((msg, type = 'info') => {
    const ts = new Date()?.toLocaleTimeString();
    setLog(prev => [...prev, { msg, type, ts }]);
  }, []);

  useEffect(() => {
    if (logEndRef?.current) {
      logEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [log]);

  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef?.current);
    }
    return () => clearInterval(timerRef?.current);
  }, [isRunning]);

  const sleep = (ms) => new Promise(res => setTimeout(res, ms));

  const updateScreenResult = (index, updates) => {
    setScreenResults(prev => {
      const next = [...prev];
      next[index] = { ...next?.[index], ...updates };
      return next;
    });
  };

  const testScreenNavigation = useCallback(async (screen, index) => {
    const start = Date.now();
    updateScreenResult(index, { status: STATUS?.RUNNING });
    setCurrentScreen(screen?.name);
    addLog(`Navigating to ${screen?.name} (${screen?.path})`, 'info');
    try {
      navigate(screen?.path);
      await sleep(600);
      const duration = Date.now() - start;
      updateScreenResult(index, { status: STATUS?.PASS, duration });
      addLog(`✓ ${screen?.name} — ${duration}ms`, 'pass');
      return true;
    } catch (err) {
      const duration = Date.now() - start;
      updateScreenResult(index, { status: STATUS?.FAIL, duration, error: err?.message || 'Navigation error' });
      addLog(`✗ ${screen?.name} — ${err?.message || 'Navigation error'}`, 'fail');
      return false;
    }
  }, [navigate, addLog]);

  const runLoginTest = useCallback(async () => {
    setPhase(PHASE?.LOGIN);
    addLog('=== PHASE 1: Login Test ===', 'phase');
    addLog(`Attempting login with: ${DEMO_EMAIL}`, 'info');
    const start = Date.now();
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: DEMO_EMAIL,
        password: DEMO_PASSWORD,
      });
      if (error) throw error;
      if (!data?.user) throw new Error('No user returned from login');
      const duration = Date.now() - start;
      setLoginResult({ status: STATUS?.PASS, duration, userId: data?.user?.id, email: data?.user?.email });
      addLog(`✓ Login successful — User: ${data?.user?.email} (${duration}ms)`, 'pass');
      return true;
    } catch (err) {
      const duration = Date.now() - start;
      setLoginResult({ status: STATUS?.FAIL, duration, error: err?.message || 'Login failed' });
      addLog(`✗ Login failed — ${err?.message || 'Unknown error'}`, 'fail');
      return false;
    }
  }, [addLog]);

  const runNavigationTests = useCallback(async () => {
    setPhase(PHASE?.NAVIGATION);
    addLog('=== PHASE 2: Navigation Test (All 38 Screens) ===', 'phase');
    let passed = 0;
    let failed = 0;
    for (let i = 0; i < ALL_SCREENS?.length; i++) {
      if (abortRef?.current) {
        addLog('Test aborted by user', 'warn');
        break;
      }
      const ok = await testScreenNavigation(ALL_SCREENS?.[i], i);
      if (ok) passed++; else failed++;
      await sleep(200);
    }
    addLog(`Navigation complete — ${passed} passed, ${failed} failed`, passed === ALL_SCREENS?.length ? 'pass' : 'warn');
    return { passed, failed };
  }, [testScreenNavigation, addLog]);

  const runAuthRefreshTest = useCallback(async () => {
    setPhase(PHASE?.AUTH_REFRESH);
    setCurrentScreen(null);
    addLog('=== PHASE 3: Auth Persistence — Page Refresh Simulation ===', 'phase');
    const start = Date.now();
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      // Get current session
      const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError) throw sessionError;
      if (!sessionData?.session) throw new Error('No active session found after navigation');
      addLog(`Session found: expires at ${new Date(sessionData.session.expires_at * 1000)?.toLocaleTimeString()}`, 'info');

      // Simulate refresh by re-fetching session (same as what happens on page reload)
      const { data: refreshData, error: refreshError } = await supabase?.auth?.refreshSession();
      if (refreshError) throw refreshError;
      if (!refreshData?.session) throw new Error('Session refresh failed — user would be logged out on page reload');

      const duration = Date.now() - start;
      setAuthRefreshResult({ status: STATUS?.PASS, duration, sessionId: refreshData?.session?.access_token?.slice(0, 20) + '...' });
      addLog(`✓ Auth persists across page refresh — Token refreshed successfully (${duration}ms)`, 'pass');
      return true;
    } catch (err) {
      const duration = Date.now() - start;
      setAuthRefreshResult({ status: STATUS?.FAIL, duration, error: err?.message });
      addLog(`✗ Auth refresh failed — ${err?.message}`, 'fail');
      return false;
    }
  }, [addLog]);

  const runAuthRestartTest = useCallback(async () => {
    setPhase(PHASE?.AUTH_RESTART);
    addLog('=== PHASE 4: Auth Persistence — Browser Restart Simulation ===', 'phase');
    const start = Date.now();
    try {
      if (!supabase) throw new Error('Supabase client not initialized');

      // Check localStorage for persisted session (Supabase stores session in localStorage)
      const storageKey = Object.keys(localStorage)?.find(k => k?.includes('supabase') && k?.includes('auth'));
      const storedSession = storageKey ? localStorage.getItem(storageKey) : null;

      if (!storedSession) {
        throw new Error('No session found in localStorage — session would not survive browser restart');
      }

      let parsedSession;
      try {
        parsedSession = JSON.parse(storedSession);
      } catch {
        throw new Error('Stored session is malformed — cannot survive browser restart');
      }

      const expiresAt = parsedSession?.expires_at || parsedSession?.session?.expires_at;
      const now = Math.floor(Date.now() / 1000);

      if (!expiresAt) {
        throw new Error('Session has no expiry — cannot verify persistence');
      }

      const expiresIn = expiresAt - now;
      if (expiresIn <= 0) {
        throw new Error('Session is expired — user would need to re-login after browser restart');
      }

      // Verify the session is valid by getting current user
      const { data: userData, error: userError } = await supabase?.auth?.getUser();
      if (userError) throw userError;
      if (!userData?.user) throw new Error('Cannot retrieve user from persisted session');

      const duration = Date.now() - start;
      const expiresInMin = Math.round(expiresIn / 60);
      setAuthRestartResult({
        status: STATUS?.PASS,
        duration,
        details: `Session stored in localStorage, expires in ~${expiresInMin} min. User: ${userData?.user?.email}`
      });
      addLog(`✓ Auth persists across browser restart — Session valid for ~${expiresInMin} more minutes (${duration}ms)`, 'pass');
      return true;
    } catch (err) {
      const duration = Date.now() - start;
      setAuthRestartResult({ status: STATUS?.FAIL, duration, error: err?.message });
      addLog(`✗ Browser restart auth check failed — ${err?.message}`, 'fail');
      return false;
    }
  }, [addLog]);

  const runAllTests = useCallback(async () => {
    abortRef.current = false;
    setIsRunning(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setLog([]);
    setLoginResult(null);
    setAuthRefreshResult(null);
    setAuthRestartResult(null);
    setOverallStatus(null);
    setScreenResults(ALL_SCREENS?.map(s => ({ ...s, status: STATUS?.PENDING, duration: null, error: null })));

    addLog('🚀 Starting Automated Test Flow', 'phase');
    addLog(`Total screens to test: ${ALL_SCREENS?.length}`, 'info');

    const loginOk = await runLoginTest();
    if (!loginOk) {
      addLog('⚠ Login failed — skipping navigation tests', 'warn');
      setScreenResults(prev => prev?.map(s => ({ ...s, status: STATUS?.SKIP })));
      setOverallStatus('fail');
      setIsRunning(false);
      setPhase(PHASE?.COMPLETE);
      return;
    }

    const { passed, failed } = await runNavigationTests();
    const refreshOk = await runAuthRefreshTest();
    const restartOk = await runAuthRestartTest();

    // Navigate back to test page
    navigate('/automated-test-flow');
    setCurrentScreen(null);
    setPhase(PHASE?.COMPLETE);

    const allPassed = loginOk && failed === 0 && refreshOk && restartOk;
    setOverallStatus(allPassed ? 'pass' : 'partial');
    addLog('=== TEST COMPLETE ===', 'phase');
    addLog(`Login: ${loginOk ? 'PASS' : 'FAIL'} | Navigation: ${passed}/${ALL_SCREENS?.length} | Auth Refresh: ${refreshOk ? 'PASS' : 'FAIL'} | Auth Restart: ${restartOk ? 'PASS' : 'FAIL'}`, allPassed ? 'pass' : 'warn');
    setIsRunning(false);
  }, [runLoginTest, runNavigationTests, runAuthRefreshTest, runAuthRestartTest, addLog, navigate]);

  const handleAbort = () => {
    abortRef.current = true;
    setIsRunning(false);
    setPhase(PHASE?.IDLE);
    addLog('Test run aborted by user', 'warn');
  };

  const handleReset = () => {
    abortRef.current = true;
    setIsRunning(false);
    setPhase(PHASE?.IDLE);
    setLoginResult(null);
    setAuthRefreshResult(null);
    setAuthRestartResult(null);
    setOverallStatus(null);
    setCurrentScreen(null);
    setElapsedTime(0);
    setLog([]);
    setScreenResults(ALL_SCREENS?.map(s => ({ ...s, status: STATUS?.PENDING, duration: null, error: null })));
  };

  const passCount = screenResults?.filter(s => s?.status === STATUS?.PASS)?.length;
  const failCount = screenResults?.filter(s => s?.status === STATUS?.FAIL)?.length;
  const skipCount = screenResults?.filter(s => s?.status === STATUS?.SKIP)?.length;

  const categories = [...new Set(ALL_SCREENS.map(s => s.category))];

  const StatusIcon = ({ status, size = 16 }) => {
    if (status === STATUS?.PASS) return <CheckCircle size={size} className="text-green-400" />;
    if (status === STATUS?.FAIL) return <XCircle size={size} className="text-red-400" />;
    if (status === STATUS?.RUNNING) return <RefreshCw size={size} className="text-blue-400 animate-spin" />;
    if (status === STATUS?.SKIP) return <AlertTriangle size={size} className="text-yellow-400" />;
    return <Clock size={size} className="text-gray-500" />;
  };

  const logColor = (type) => {
    if (type === 'pass') return 'text-green-400';
    if (type === 'fail') return 'text-red-400';
    if (type === 'warn') return 'text-yellow-400';
    if (type === 'phase') return 'text-blue-300 font-semibold';
    return 'text-gray-300';
  };

  const formatTime = (s) => `${Math.floor(s / 60)?.toString()?.padStart(2, '0')}:${(s % 60)?.toString()?.padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Monitor size={28} className="text-blue-400" />
            <h1 className="text-2xl font-bold text-white">Automated Test Flow</h1>
            {overallStatus === 'pass' && (
              <span className="px-3 py-1 bg-green-900/50 text-green-400 rounded-full text-sm font-medium border border-green-700">ALL PASSED</span>
            )}
            {overallStatus === 'partial' && (
              <span className="px-3 py-1 bg-yellow-900/50 text-yellow-400 rounded-full text-sm font-medium border border-yellow-700">PARTIAL PASS</span>
            )}
            {overallStatus === 'fail' && (
              <span className="px-3 py-1 bg-red-900/50 text-red-400 rounded-full text-sm font-medium border border-red-700">FAILED</span>
            )}
          </div>
          <p className="text-gray-400 text-sm">Login → Navigate all {ALL_SCREENS?.length} screens → Verify persistent auth state</p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 mb-6">
          {!isRunning ? (
            <button
              onClick={runAllTests}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
            >
              <Play size={16} />
              {phase === PHASE?.COMPLETE ? 'Run Again' : 'Run All Tests'}
            </button>
          ) : (
            <button
              onClick={handleAbort}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-700 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
            >
              <XCircle size={16} />
              Abort
            </button>
          )}
          <button
            onClick={handleReset}
            disabled={isRunning}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded-lg font-medium transition-colors"
          >
            <RefreshCw size={16} />
            Reset
          </button>
          {isRunning && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <RefreshCw size={14} className="animate-spin" />
              <span>Running... {formatTime(elapsedTime)}</span>
              {currentScreen && <span className="text-blue-400">→ {currentScreen}</span>}
            </div>
          )}
          {!isRunning && elapsedTime > 0 && (
            <span className="text-gray-500 text-sm">Completed in {formatTime(elapsedTime)}</span>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Left: Phase Results + Screen List */}
          <div className="xl:col-span-2 space-y-4">

            {/* Phase Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Login Phase */}
              <div className={`rounded-lg border p-3 ${
                loginResult?.status === STATUS?.PASS ? 'border-green-700 bg-green-900/20' :
                loginResult?.status === STATUS?.FAIL ? 'border-red-700 bg-red-900/20': 'border-gray-700 bg-gray-900'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <LogIn size={14} className="text-gray-400" />
                  <span className="text-xs text-gray-400 font-medium">LOGIN</span>
                </div>
                {loginResult ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <StatusIcon status={loginResult?.status} size={14} />
                      <span className={`text-sm font-semibold ${loginResult?.status === STATUS?.PASS ? 'text-green-400' : 'text-red-400'}`}>
                        {loginResult?.status === STATUS?.PASS ? 'PASS' : 'FAIL'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{loginResult?.duration}ms</p>
                    {loginResult?.error && <p className="text-xs text-red-400 mt-0.5 truncate">{loginResult?.error}</p>}
                  </>
                ) : (
                  <span className="text-xs text-gray-600">Pending</span>
                )}
              </div>

              {/* Navigation Phase */}
              <div className={`rounded-lg border p-3 ${
                phase === PHASE?.COMPLETE || passCount + failCount > 0 ?
                  failCount === 0 ? 'border-green-700 bg-green-900/20' : 'border-yellow-700 bg-yellow-900/20' :'border-gray-700 bg-gray-900'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Navigation size={14} className="text-gray-400" />
                  <span className="text-xs text-gray-400 font-medium">NAVIGATION</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-green-400">{passCount}</span>
                  <span className="text-xs text-gray-500">/</span>
                  <span className="text-sm font-semibold text-gray-300">{ALL_SCREENS?.length}</span>
                </div>
                {failCount > 0 && <p className="text-xs text-red-400 mt-0.5">{failCount} failed</p>}
                {skipCount > 0 && <p className="text-xs text-yellow-400 mt-0.5">{skipCount} skipped</p>}
              </div>

              {/* Auth Refresh Phase */}
              <div className={`rounded-lg border p-3 ${
                authRefreshResult?.status === STATUS?.PASS ? 'border-green-700 bg-green-900/20' :
                authRefreshResult?.status === STATUS?.FAIL ? 'border-red-700 bg-red-900/20': 'border-gray-700 bg-gray-900'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw size={14} className="text-gray-400" />
                  <span className="text-xs text-gray-400 font-medium">PAGE REFRESH</span>
                </div>
                {authRefreshResult ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <StatusIcon status={authRefreshResult?.status} size={14} />
                      <span className={`text-sm font-semibold ${authRefreshResult?.status === STATUS?.PASS ? 'text-green-400' : 'text-red-400'}`}>
                        {authRefreshResult?.status === STATUS?.PASS ? 'PASS' : 'FAIL'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{authRefreshResult?.duration}ms</p>
                  </>
                ) : (
                  <span className="text-xs text-gray-600">Pending</span>
                )}
              </div>

              {/* Auth Restart Phase */}
              <div className={`rounded-lg border p-3 ${
                authRestartResult?.status === STATUS?.PASS ? 'border-green-700 bg-green-900/20' :
                authRestartResult?.status === STATUS?.FAIL ? 'border-red-700 bg-red-900/20': 'border-gray-700 bg-gray-900'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Shield size={14} className="text-gray-400" />
                  <span className="text-xs text-gray-400 font-medium">BROWSER RESTART</span>
                </div>
                {authRestartResult ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <StatusIcon status={authRestartResult?.status} size={14} />
                      <span className={`text-sm font-semibold ${authRestartResult?.status === STATUS?.PASS ? 'text-green-400' : 'text-red-400'}`}>
                        {authRestartResult?.status === STATUS?.PASS ? 'PASS' : 'FAIL'}
                      </span>
                    </div>
                    {authRestartResult?.details && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{authRestartResult?.details}</p>
                    )}
                    {authRestartResult?.error && (
                      <p className="text-xs text-red-400 mt-0.5 truncate">{authRestartResult?.error}</p>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-gray-600">Pending</span>
                )}
              </div>
            </div>

            {/* Screen Results by Category */}
            {categories?.map(cat => {
              const catScreens = screenResults?.filter(s => s?.category === cat);
              return (
                <div key={cat} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-800/60 border-b border-gray-700 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-200">{cat}</span>
                    <span className="text-xs text-gray-500">
                      {catScreens?.filter(s => s?.status === STATUS?.PASS)?.length}/{catScreens?.length} passed
                    </span>
                  </div>
                  <div className="divide-y divide-gray-800">
                    {catScreens?.map((screen, idx) => {
                      const globalIdx = screenResults?.findIndex(s => s?.path === screen?.path);
                      return (
                        <div key={screen?.path} className={`flex items-center gap-3 px-4 py-2.5 ${
                          screen?.status === STATUS?.RUNNING ? 'bg-blue-900/10' : ''
                        }`}>
                          <StatusIcon status={screen?.status} size={15} />
                          <span className="flex-1 text-sm text-gray-300 truncate">{screen?.name}</span>
                          <span className="text-xs text-gray-600 font-mono">{screen?.path}</span>
                          {screen?.duration && (
                            <span className="text-xs text-gray-500 w-14 text-right">{screen?.duration}ms</span>
                          )}
                          {screen?.error && (
                            <span className="text-xs text-red-400 max-w-32 truncate" title={screen?.error}>{screen?.error}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: Live Log */}
          <div className="xl:col-span-1">
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden sticky top-4">
              <div className="px-4 py-2.5 bg-gray-800/60 border-b border-gray-700 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-200">Live Log</span>
                <span className="text-xs text-gray-500">{log?.length} entries</span>
              </div>
              <div className="h-[600px] overflow-y-auto p-3 font-mono text-xs space-y-1">
                {log?.length === 0 && (
                  <p className="text-gray-600 italic">Logs will appear here when tests run...</p>
                )}
                {log?.map((entry, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-gray-600 shrink-0">{entry?.ts}</span>
                    <span className={logColor(entry?.type)}>{entry?.msg}</span>
                  </div>
                ))}
                <div ref={logEndRef} />
              </div>
            </div>
          </div>
        </div>

        {/* Auth Details */}
        {(authRefreshResult || authRestartResult) && (
          <div className="mt-4 bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-200 mb-3">Auth Persistence Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              {authRefreshResult && (
                <div className={`p-3 rounded border ${
                  authRefreshResult?.status === STATUS?.PASS ? 'border-green-700 bg-green-900/10' : 'border-red-700 bg-red-900/10'
                }`}>
                  <p className="font-semibold text-gray-300 mb-1">Page Refresh Test</p>
                  <p className="text-gray-400">Method: supabase.auth.refreshSession()</p>
                  <p className="text-gray-400">Duration: {authRefreshResult?.duration}ms</p>
                  {authRefreshResult?.sessionId && <p className="text-gray-400">Token: {authRefreshResult?.sessionId}</p>}
                  {authRefreshResult?.error && <p className="text-red-400">Error: {authRefreshResult?.error}</p>}
                </div>
              )}
              {authRestartResult && (
                <div className={`p-3 rounded border ${
                  authRestartResult?.status === STATUS?.PASS ? 'border-green-700 bg-green-900/10' : 'border-red-700 bg-red-900/10'
                }`}>
                  <p className="font-semibold text-gray-300 mb-1">Browser Restart Test</p>
                  <p className="text-gray-400">Method: localStorage session persistence check</p>
                  {authRestartResult?.details && <p className="text-gray-400">{authRestartResult?.details}</p>}
                  {authRestartResult?.error && <p className="text-red-400">Error: {authRestartResult?.error}</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
